import { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { useSearchParams } from "react-router-dom";
import axiosInstance from "../../axiosInstance";
import PublicLayout from "../components/PublicLayout";
import JobFiltersPanel from "../../components/job/JobFiltersPanel";
import PublicJobCard from "../components/PublicJobCard";

const DEFAULT_FILTERS = {
  keyword: "",
  jobTitle: "",
  location: "",
  department: "",
  skill: "",
  employmentType: "",
  workMode: "",
  experienceMin: "",
  experienceMax: "",
  salaryMin: "",
  salaryMax: "",
  sort: "relevant",
};

const PublicJobs = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [jobs, setJobs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [showFilters, setShowFilters] = useState(false);
  const paramsKey = searchParams.toString();

  const currentPage = Number(searchParams.get("page") || 1);

  useEffect(() => {
    setFilters({
      keyword: searchParams.get("keyword") || searchParams.get("search") || "",
      jobTitle: searchParams.get("jobTitle") || "",
      location: searchParams.get("location") || searchParams.get("state") || "",
      department: searchParams.get("department") || "",
      skill: searchParams.get("skill") || "",
      employmentType: searchParams.get("employmentType") || "",
      workMode: searchParams.get("workMode") || "",
      experienceMin: searchParams.get("experienceMin") || "",
      experienceMax: searchParams.get("experienceMax") || "",
      salaryMin: searchParams.get("salaryMin") || "",
      salaryMax: searchParams.get("salaryMax") || "",
      sort: searchParams.get("sort") || "relevant",
    });
  }, [searchParams]);

  useEffect(() => {
    const fetchJobs = async () => {
      try {
        setLoading(true);
        const params = new URLSearchParams(searchParams);
        params.set("limit", "9");

        const [jobsResponse, categoryResponse, departmentResponse] = await Promise.all([
          axiosInstance.get(`/job-portal/jobs/public?${params.toString()}`),
          axiosInstance.get("/get-all-specialization"),
          axiosInstance.get("/get-all-department"),
        ]);

        setJobs(jobsResponse.data?.data?.jobs || []);
        setMeta(jobsResponse.data?.meta || null);
        const allCategories = [
          ...(categoryResponse.data?.Specialization || []).map((item) => item.name),
          ...(departmentResponse.data?.Department || []).map((item) => item.name),
        ];
        setCategories([...new Set(allCategories)].filter(Boolean));
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    };

    fetchJobs();
  }, [searchParams, paramsKey]);

  useEffect(() => {
    setShowFilters(false);
  }, [paramsKey]);

  const updateParam = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (value) {
      next.set(key, value);
    } else {
      next.delete(key);
    }
    if (key !== "page") {
      next.set("page", "1");
    }
    setSearchParams(next);
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const applyFilters = (event) => {
    event.preventDefault();
    const next = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value) next.set(key, value);
    });
    next.set("page", "1");
    setSearchParams(next);
  };

  const resetFilters = () => {
    setFilters(DEFAULT_FILTERS);
    setSearchParams(new URLSearchParams({ sort: "relevant", page: "1" }));
  };

  return (
    <PublicLayout>
      <Helmet>
        <title>Professional Jobs | YNB Portal</title>
        <meta
          name="description"
          content="Browse curated roles with advanced search, clean filters, and an efficient job search experience."
        />
      </Helmet>

      <section className="portal-hero border-bottom">
        <div className="portal-container">
          <div className="portal-hero-inner mb-0">
            <span className="portal-kicker">Find Jobs</span>
            <h1 className="h1 fw-bold mb-2">Discover your career path</h1>
            <p className="portal-subtitle mx-auto">
              Search open roles by keyword, job type, salary range, and experience level.
            </p>

            <form onSubmit={applyFilters} className="portal-search-bar mt-4" aria-label="Job search filters">
              <input
                className="portal-input"
                value={filters.keyword}
                onChange={(event) => handleFilterChange("keyword", event.target.value)}
                placeholder="Job title or keyword"
                aria-label="Job title or keyword"
              />
              <select
                className="portal-select"
                value={filters.employmentType}
                onChange={(event) => handleFilterChange("employmentType", event.target.value)}
                aria-label="Job type"
              >
                <option value="">Job Type</option>
                <option value="full_time">Full Time</option>
                <option value="part_time">Part Time</option>
                <option value="contract">Contract</option>
                <option value="internship">Internship</option>
              </select>
              <select
                className="portal-select"
                value={filters.salaryMin}
                onChange={(event) => handleFilterChange("salaryMin", event.target.value)}
                aria-label="Minimum salary"
              >
                <option value="">Salary Range</option>
                <option value="30000">30k+</option>
                <option value="60000">60k+</option>
                <option value="100000">100k+</option>
              </select>
              <select
                className="portal-select"
                value={filters.experienceMin}
                onChange={(event) => handleFilterChange("experienceMin", event.target.value)}
                aria-label="Experience level"
              >
                <option value="">Experience</option>
                <option value="0">Entry Level</option>
                <option value="2">Mid Level</option>
                <option value="5">Senior</option>
              </select>
              <button type="submit" className="portal-btn-primary">
                Search Jobs
              </button>
            </form>

            <button
              type="button"
              className="portal-btn-secondary d-md-none mt-3"
              onClick={() => setShowFilters((prev) => !prev)}
            >
              {showFilters ? "Hide Filters" : "Search and Filter"}
            </button>
          </div>
        </div>
      </section>

      <section className="portal-section">
        <div className="portal-container">
          {showFilters ? (
            <div className="portal-mobile-filter-layer d-lg-none">
              <button
                type="button"
                className="portal-mobile-filter-backdrop"
                onClick={() => setShowFilters(false)}
                aria-label="Close filters"
              ></button>
              <div className="portal-mobile-filter-panel">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h2 className="h5 fw-bold mb-0">Search Filters</h2>
                  <button
                    type="button"
                    className="portal-btn-secondary"
                    onClick={() => setShowFilters(false)}
                  >
                    Close
                  </button>
                </div>
                <JobFiltersPanel
                  filters={filters}
                  onChange={handleFilterChange}
                  onSubmit={(event) => {
                    applyFilters(event);
                    setShowFilters(false);
                  }}
                  onReset={resetFilters}
                  categories={categories}
                  loading={loading}
                />
              </div>
            </div>
          ) : null}

          <div className="row">
            <aside className="col-lg-3 d-none d-lg-block">
              <div className="sticky-top" style={{ top: "100px" }}>
                <JobFiltersPanel
                  filters={filters}
                  onChange={handleFilterChange}
                  onSubmit={applyFilters}
                  onReset={resetFilters}
                  categories={categories}
                  loading={loading}
                />
              </div>
            </aside>

            <div className="col-lg-9">
              <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
                <div>
                  <h2 className="h4 fw-bold mb-1">Results ({meta?.total || jobs.length})</h2>
                  <p className="small text-muted mb-0">Discovering the best matches for your profile.</p>
                </div>
                <div className="d-flex align-items-center gap-2">
                  <span className="small text-muted">Sort:</span>
                  <select
                    className="portal-select py-1 px-2 border"
                    style={{ minWidth: "170px" }}
                    value={filters.sort}
                    onChange={(event) => {
                      handleFilterChange("sort", event.target.value);
                      updateParam("sort", event.target.value);
                    }}
                  >
                    <option value="relevant">Relevant</option>
                    <option value="newest">Newest</option>
                    <option value="oldest">Oldest</option>
                    <option value="salary_high">Salary (High to Low)</option>
                    <option value="salary_low">Salary (Low to High)</option>
                  </select>
                </div>
              </div>

              {loading ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary" role="status"></div>
                  <div className="mt-3 text-muted">Retrieving best matches...</div>
                </div>
              ) : jobs.length === 0 ? (
                <div className="text-center py-5 border rounded bg-light">
                  <h3 className="h5 fw-bold">No jobs match your criteria</h3>
                  <p className="text-muted small">Try adjusting your filters or reset to view all active roles.</p>
                  <button className="portal-btn-primary mt-3" onClick={resetFilters}>
                    Reset All Filters
                  </button>
                </div>
              ) : (
                <>
                  <div className="portal-job-grid">
                    {jobs.map((job) => (
                      <PublicJobCard job={job} key={job.id} />
                    ))}
                  </div>

                  <div className="d-flex justify-content-between align-items-center mt-5 pt-4 border-top">
                    <button
                      className="portal-btn-secondary"
                      disabled={currentPage <= 1}
                      onClick={() => updateParam("page", String(currentPage - 1))}
                    >
                      Previous
                    </button>
                    <div className="text-muted small">
                      Page <strong>{meta?.page || 1}</strong> of <strong>{meta?.totalPages || 1}</strong>
                    </div>
                    <button
                      className="portal-btn-secondary"
                      disabled={currentPage >= (meta?.totalPages || 1)}
                      onClick={() => updateParam("page", String(currentPage + 1))}
                    >
                      Next
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
};

export default PublicJobs;
